import { memo, useCallback, useMemo } from "react";
import ChooseOneGroup from "./ChooseOneGroup";
import MultiSelectGroup from "./MultiSelectGroup";
import NestedOptionsGroup from "./NestedOptionsGroup";
import {
  ChooseOneValue,
  GroupValue,
  OptionGroupDef,
} from "../readmission-f2f-types";
import {
  emptyChooseOneValue,
  emptyGroupValue,
  isGroupValue,
} from "../readmission-f2f-utils";

export interface OptionGroupItemProps {
  group: OptionGroupDef;
  value: GroupValue | ChooseOneValue | undefined;
  onChange: (key: string, value: GroupValue | ChooseOneValue) => void;
  bordered: boolean;
}

/** One option group; memoised so a change in a sibling group does not re-render its checkboxes. */
const OptionGroupItem = memo(
  ({ group, value, onChange, bordered }: OptionGroupItemProps) => {
    const handleChange = useCallback(
      (next: GroupValue | ChooseOneValue) => onChange(group.key, next),
      [onChange, group.key]
    );
    const groupValue = useMemo(
      () => (value && isGroupValue(value) ? value : emptyGroupValue()),
      [value]
    );
    const chooseOneValue = useMemo(
      () => (value && !isGroupValue(value) ? value : emptyChooseOneValue()),
      [value]
    );

    if (group.chooseOne) {
      return (
        <ChooseOneGroup
          title={group.title}
          options={group.options}
          value={chooseOneValue}
          onChange={handleChange}
          withFreeText={group.withFreeText}
          detailPlaceholders={group.optionDetailPlaceholders}
          helperTexts={group.optionHelperText}
          freeTextPlaceholder={group.freeTextPlaceholder}
          bordered={bordered}
          isInline={group.isInline}
        />
      );
    }
    if (group.nested) {
      return (
        <NestedOptionsGroup
          group={group}
          nested={group.nested}
          value={groupValue}
          onChange={handleChange}
        />
      );
    }
    return (
      <MultiSelectGroup
        title={group.title}
        options={group.options}
        value={groupValue}
        onChange={handleChange}
        exclusive={group.exclusive}
        withDetails={group.withDetails}
        withCatchAll={group.withFreeText}
        detailPlaceholder={group.detailPlaceholder}
        optionDetailPlaceholders={group.optionDetailPlaceholders}
        optionDetailDefaults={group.optionDetailDefaults}
        freeTextPlaceholder={group.freeTextPlaceholder}
        freeTextLabel={group.freeTextLabel}
        freeTextRows={group.freeTextRows}
        hideTitle={group.hideTitle}
        filter={group.filter}
        optionDateDetails={group.optionDateDetails}
        inputMask={group.inputMask}
        inputMaskError={group.inputMaskError}
      />
    );
  }
);

OptionGroupItem.displayName = "OptionGroupItem";

export default OptionGroupItem;
